/**
 * `InstagramCommentList` — the `/inbox` **Instagram comments** tab
 * (2026-08-07).
 *
 * The sibling of `ChatList`, and deliberately NOT a reuse of it: a comment is
 * not a conversation. There is no thread to open, no 24-hour window, no sender
 * to resolve to a member — just a public remark under one of the studio's own
 * posts, and a reply that is itself public and permanent.
 *
 * Three things this component exists to get right:
 *
 *   1. **`handle unavailable` is rendered, never invented.** Meta withheld the
 *      comment author by SILENT FIELD OMISSION until `instagram_manage_comments`
 *      was granted 2026-08-07 — HTTP 200 with the key simply absent. Authors
 *      resolve now, so an authorless row is a REGRESSION signal, and the banner
 *      below says so out loud rather than letting an anonymous board look
 *      normal.
 *   2. **The permalink is the durable link; the thumbnail is a cache.**
 *      Instagram CDN URLs are signed and expire within days, so the tile falls
 *      back to a plain "View post" link rather than rendering a broken image.
 *   3. **Replying is a deliberate act.** The composer is per-comment and posts
 *      through `reply_instagram_comment`, whose actor matrix is staff + CLI
 *      only. The copy says "public" because the operator is about to say
 *      something under their own post that they cannot edit afterwards.
 *
 * 2026-09-24 — the funnel approval block joins it (slice 3). A staged DM
 * renders UNDER the comment that triggered it, because that comment is the only
 * context in which "should we send this?" is answerable.
 *
 * ⚠️ **`allowFunnelActions` is a safety gate, not a preference.** `/inbox`
 * renders this component ALONE, with no tab strip, for Meta App Review
 * (`mailForbidden`). A reviewer tapping Approve would cold-DM a real member of
 * the public, so the whole block is withheld from that view. The comment list
 * itself is unchanged there — it is public data and always was.
 */

import { useState, type JSX } from "react";
import { useDispatch } from "../lib/use-dispatch";
import type {
  InstagramCommentRow,
  InstagramCommentsResponse,
  InstagramFunnelStagedSend,
} from "../lib/api";
import {
  describeFunnelSend,
  funnelExpiryState,
  funnelSendability,
  publicReplyOnApprove,
  FUNNEL_BODY_MAX,
} from "../lib/instagram-funnel-approval";

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function kindLabel(kind: InstagramCommentRow["kind"]): string {
  return kind === "mention"
    ? "mention"
    : kind === "live_comment"
      ? "live comment"
      : "comment";
}

function CommentReplyComposer({
  comment,
  onReplied,
}: {
  readonly comment: InstagramCommentRow;
  readonly onReplied: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState<string>("");
  const dispatch = useDispatch<
    { readonly engagement_event_id: string; readonly body: string },
    { readonly reply_comment_id: string }
  >("reply_instagram_comment");

  const trimmed = draft.trim();
  const canSend = trimmed.length > 0 && !dispatch.loading;

  const send = async (): Promise<void> => {
    if (!canSend) return;
    const r = await dispatch.run({
      engagement_event_id: comment.id,
      body: trimmed,
    });
    if (r.ok) {
      setDraft("");
      onReplied();
    }
  };

  return (
    <div className="ig-comment-composer">
      <textarea
        className="dc-textarea"
        rows={2}
        value={draft}
        placeholder="Write a public reply…"
        onChange={(e) => setDraft(e.target.value)}
        // Meta's comment cap. The chokepoint enforces it too (JSON Schema is
        // the SOLE enforcement layer); this only stops the operator typing
        // past it and losing the tail.
        maxLength={2200}
      />
      <div className="ig-comment-composer-actions">
        <button
          type="button"
          className="row-action-btn"
          disabled={!canSend}
          onClick={() => void send()}
        >
          {dispatch.loading ? "Replying…" : "Reply publicly"}
        </button>
        <span className="dc-muted">
          Posts under the studio&apos;s post. Public and permanent — Instagram
          has no edit.
        </span>
      </div>
      {dispatch.error !== null ? (
        <p className="dc-error">
          Reply failed: {dispatch.error.code} — {dispatch.error.message}
        </p>
      ) : null}
    </div>
  );
}

/**
 * One staged funnel DM, rendered under the comment that triggered it.
 *
 * The interaction is Approve · Edit & send · Skip, and Approve is a CONFIRM
 * STEP rather than a click — the `PendingApprovalsPanel` pattern, for the same
 * reason and a sharper one. Meta allows exactly ONE private reply per comment,
 * ever: a mis-tap is not "send it again properly", it is a stranger receiving
 * the wrong message and no way to follow up on that comment.
 *
 * "Edit & send" is the same button with the body changed. It sends what is in
 * the box and writes that back to the row, so the record says what the person
 * actually read. It deliberately does NOT touch the campaign — changing the
 * template for everyone is a different decision and belongs on the campaigns
 * page, not behind a button someone is pressing to deal with one comment.
 */
function FunnelApprovalBlock({
  send,
  commentId,
  onResolved,
}: {
  readonly send: InstagramFunnelStagedSend;
  /**
   * `instagram_engagement_events.id` — the COMMENT, which is what
   * `reply_instagram_comment` is addressed by. Deliberately a separate prop
   * rather than a field on the send: they are two different rows, and the one
   * time they got conflated the public reply was addressed by the send's id.
   */
  readonly commentId: string;
  readonly onResolved: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState<string>(send.staged_body);
  const [mode, setMode] = useState<"idle" | "confirm">("idle");
  // The public reply is a SECOND dispatch and can fail on its own. Its failure
  // must never look like the DM failing — the DM is already delivered and
  // cannot be recalled, so the operator needs to know precisely which half is
  // outstanding.
  const [publicReplyNote, setPublicReplyNote] = useState<string | null>(null);

  const sendDm = useDispatch<
    { readonly funnel_send_id: string; readonly body?: string },
    { readonly message_id: string }
  >("send_instagram_private_reply");
  const skipSend = useDispatch<
    { readonly funnel_send_id: string },
    { readonly status: string }
  >("skip_instagram_funnel_send");
  const publicReply = useDispatch<
    { readonly engagement_event_id: string; readonly body: string },
    { readonly reply_comment_id: string }
  >("reply_instagram_comment");

  const now = Date.now();
  const busy = sendDm.loading || skipSend.loading || publicReply.loading;
  const sendability = funnelSendability(send, draft, now);
  const expiry = funnelExpiryState(send.private_reply_expires_at, now);
  const alsoPosts = publicReplyOnApprove(send);

  // A resolved row keeps its place in the list but stops offering buttons:
  // the comment above it is still the context for what happened.
  if (send.status !== "staged") {
    return (
      <div className="ig-funnel-block ig-funnel-resolved">
        <span className="ig-funnel-campaign">{send.campaign_name}</span>
        <span className="dc-muted"> · {describeFunnelSend(send, now)}</span>
      </div>
    );
  }

  const approve = async (): Promise<void> => {
    if (!sendability.canSend || busy) return;
    const r = await sendDm.run({
      funnel_send_id: send.id,
      // Send `body` ONLY when it differs. An unedited approve leaves the
      // staged body authoritative rather than re-asserting it from the
      // browser, so a stale tab cannot quietly resurrect old copy.
      ...(sendability.edited ? { body: draft.trim() } : {}),
    });
    if (!r.ok) return;

    // The DM is out and irreversible from here. Everything below is best-effort
    // and reported as such.
    if (alsoPosts !== null) {
      const pr = await publicReply.run({
        engagement_event_id: commentId,
        body: alsoPosts,
      });
      setPublicReplyNote(
        pr.ok
          ? null
          : "The DM was sent. The public reply was NOT posted — post it yourself under the comment if you still want it.",
      );
    }
    setMode("idle");
    onResolved();
  };

  const skip = async (): Promise<void> => {
    if (busy) return;
    const r = await skipSend.run({ funnel_send_id: send.id });
    if (r.ok) onResolved();
  };

  return (
    <div
      className={
        expiry.urgent ? "ig-funnel-block ig-funnel-urgent" : "ig-funnel-block"
      }
    >
      <div className="ig-funnel-head">
        <span className="ig-funnel-campaign">{send.campaign_name}</span>
        <span className="dc-muted">{describeFunnelSend(send, now)}</span>
      </div>

      {send.mode === "auto" ? (
        // Say it out loud: this row will send with or without the operator, so
        // a row that disappears on its own is expected, not a glitch.
        <p className="dc-muted">
          This campaign is on <strong>auto</strong> — it sends without a tap.
          The buttons below just let you get ahead of it, or stop this one.
        </p>
      ) : null}

      <textarea
        className="dc-textarea"
        rows={3}
        value={draft}
        disabled={busy}
        onChange={(e) => {
          setDraft(e.target.value);
          // Editing after opening the confirm step invalidates what was
          // confirmed. Drop back rather than send something unread.
          setMode("idle");
        }}
        maxLength={FUNNEL_BODY_MAX}
      />

      <p className="dc-muted">
        Goes as a direct message with a “{send.chip_label}” button.{" "}
        {alsoPosts !== null ? (
          <>
            Approving also posts “{alsoPosts}” publicly under the comment.
          </>
        ) : (
          <>No public reply is posted.</>
        )}
      </p>

      {sendability.blockedReason !== null ? (
        <p className={expiry.expired ? "dc-error" : "dc-muted"}>
          Cannot send: {sendability.blockedReason}
        </p>
      ) : null}

      {mode === "idle" ? (
        <div className="ig-funnel-actions">
          <button
            type="button"
            className="row-action-btn"
            disabled={!sendability.canSend || busy}
            onClick={() => setMode("confirm")}
          >
            {sendability.edited ? "Edit & send…" : "Approve…"}
          </button>
          <button
            type="button"
            className="row-action-btn"
            disabled={busy}
            onClick={() => void skip()}
          >
            {skipSend.loading ? "Skipping…" : "Skip"}
          </button>
        </div>
      ) : (
        <div className="pa-approval-confirm">
          {/* A confirm step, not a click. Meta allows ONE private reply per
              comment — there is no second attempt to correct a mis-tap with. */}
          <p>
            This sends a real Instagram DM to{" "}
            <strong>someone who has not messaged the studio</strong>. Meta allows
            one private reply per comment, so it cannot be repeated or undone.
          </p>
          <div className="ig-funnel-actions">
            <button
              type="button"
              className="row-action-btn"
              disabled={busy}
              onClick={() => void approve()}
            >
              {sendDm.loading
                ? "Sending…"
                : sendability.edited
                  ? "Yes — send my edited message"
                  : "Yes — send it"}
            </button>
            <button
              type="button"
              className="row-action-btn"
              disabled={busy}
              onClick={() => setMode("idle")}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {sendDm.error !== null ? (
        <p className="dc-error">
          Send failed: {sendDm.error.code} — {sendDm.error.message}
        </p>
      ) : null}
      {skipSend.error !== null ? (
        <p className="dc-error">
          Skip failed: {skipSend.error.code} — {skipSend.error.message}
        </p>
      ) : null}
      {publicReplyNote !== null ? (
        <p className="dc-error">{publicReplyNote}</p>
      ) : null}
    </div>
  );
}

export function InstagramCommentList({
  board,
  onReplied,
  allowFunnelActions = true,
}: {
  readonly board: InstagramCommentsResponse | null;
  readonly onReplied?: () => void;
  /**
   * Render the funnel approval block. FALSE in the Meta App Review view, where
   * this component is shown alone: a reviewer tapping Approve would cold-DM a
   * real person. Defaults true so the ordinary `/inbox` needs no ceremony.
   */
  readonly allowFunnelActions?: boolean;
}): JSX.Element {
  if (board === null) {
    return (
      <p className="empty">
        Instagram comments could not be loaded. The mail pipeline above is
        unaffected.
      </p>
    );
  }
  if (board.comments.length === 0) {
    return (
      <p className="empty">
        No Instagram comments yet. They arrive on the `comments` webhook and
        appear here within about a minute.
      </p>
    );
  }

  return (
    <section className="pr-stage">
      {/* A wholly-authorless board is the silent-omission failure mode, not a
          quiet day — say so rather than letting it look normal. */}
      {board.counts.awaiting_funnel_approval > 0 && allowFunnelActions ? (
        <p className="subtle">
          {board.counts.awaiting_funnel_approval} staged funnel DM
          {board.counts.awaiting_funnel_approval === 1 ? "" : "s"} awaiting your
          approval. Each one expires with its comment&apos;s 7-day window,
          whether or not anyone looks.
        </p>
      ) : null}

      {board.counts.authorless > 0 ? (
        <p
          className={
            board.counts.authorless === board.counts.total
              ? "dc-error"
              : "subtle"
          }
        >
          {board.counts.authorless} of {board.counts.total} comment
          {board.counts.total === 1 ? "" : "s"} arrived with no readable author
          handle
          {board.counts.authorless === board.counts.total
            ? " — that is every one of them, which usually means the instagram_manage_comments scope has lapsed."
            : "."}
        </p>
      ) : null}

      <div className="table-scroll-x">
        <table className="pr-people-table table-cards">
          <thead>
            <tr>
              <th>Who</th>
              <th>Comment</th>
              <th>Post</th>
              <th>When</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {board.comments.map((cm) => (
              <tr key={cm.id}>
                <td className="table-card-title">
                  {cm.author_username !== null ? (
                    <a
                      href={`https://www.instagram.com/${cm.author_username}/`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      @{cm.author_username}
                    </a>
                  ) : (
                    <span className="dc-muted" title="Meta did not return the author for this comment">
                      handle unavailable
                    </span>
                  )}
                  <div className="dc-muted">{kindLabel(cm.kind)}</div>
                </td>
                <td data-label="Comment">
                  {cm.text ?? <span className="dc-muted">(no text)</span>}
                  {/* The staged DM sits directly under the comment that caused
                      it — the only context in which "should we send this?" can
                      be answered. Withheld entirely from the App Review view. */}
                  {allowFunnelActions
                    ? cm.funnel_sends.map((fs) => (
                        <FunnelApprovalBlock
                          key={fs.id}
                          send={fs}
                          commentId={cm.id}
                          onResolved={() => onReplied?.()}
                        />
                      ))
                    : null}
                  {cm.replied_at === null && cm.kind !== "mention" ? (
                    <CommentReplyComposer
                      comment={cm}
                      onReplied={() => onReplied?.()}
                    />
                  ) : null}
                </td>
                <td data-label="Post">
                  {cm.media_permalink !== null ? (
                    <a
                      href={cm.media_permalink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View post
                    </a>
                  ) : (
                    <span className="dc-muted">—</span>
                  )}
                </td>
                <td data-label="When" title={cm.occurred_at}>
                  {relativeTime(cm.occurred_at)}
                  {/* The ingest clock, not the comment's own time — enrichment
                      has not run (or failed). Marked so nobody reads an
                      approximate timestamp as exact. */}
                  {cm.time_is_approximate ? (
                    <span className="dc-muted" title="Ingest time — the comment's own timestamp has not been read yet">
                      {" "}
                      ~
                    </span>
                  ) : null}
                </td>
                <td data-label="Status">
                  {cm.replied_at !== null ? (
                    <span className="dc-muted">replied</span>
                  ) : cm.kind === "mention" ? (
                    <span className="dc-muted">no reply target</span>
                  ) : (
                    <span>awaiting reply</span>
                  )}
                  {cm.enrichment_error !== null ? (
                    <div className="dc-muted" title={cm.enrichment_error}>
                      enrichment failed
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
